import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { formatClockTime } from '../internals/localTime';
import {
  resolveUrgency,
  urgencyTitle,
  type UrgencyTone,
} from '../internals/urgency';
import { useTimeFormat } from '../internals/useTimeFormat';
import { sizingClass, TEXT_STYLE, TONE_CLASS } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

const URGENCY_TONE_CLASS: Record<UrgencyTone, string> = {
  default: TONE_CLASS.muted,
  soon: TONE_CLASS.warn,
  late: TONE_CLASS.danger,
};

const RECOMPUTE_INTERVAL_MS = 60_000;

export const Urgency: ComponentFn<typeof catalog, 'Urgency'> = ({ props }) => {
  const { label, actBy, tone } = props;
  const prefer12Hr = useTimeFormat();

  // Ticks the countdown once a minute; resolveUrgency itself reads the clock fresh
  // on every render, so the tick only needs to trigger the render, not carry a value
  const [, tick] = useState(0);
  useEffect(() => {
    if (!actBy) return;
    const id = setInterval(() => tick((n) => n + 1), RECOMPUTE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [actBy]);

  const resolved =
    props.mode === 'event' && actBy
      ? {
          label: `${label} ${formatClockTime(new Date(actBy), prefer12Hr)}`,
          tone: tone ?? 'default',
        }
      : resolveUrgency({ label, actBy, tone });

  return (
    <span
      title={actBy ? urgencyTitle(actBy, prefer12Hr) : undefined}
      // Never squeezed beside a title; alone past the row it ends in an ellipsis
      className={cn(
        'max-w-full shrink-0 truncate',
        sizingClass(props.sizing),
        TEXT_STYLE.meta,
        URGENCY_TONE_CLASS[resolved.tone]
      )}
    >
      {resolved.label}
    </span>
  );
};
